import React, { useEffect, useMemo, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { Header, Title, TopBar } from "@/components";
import { TopBarItem } from "@/components/TopBar/types";
import { useAppDispatch, useAppSelector } from "@/redux/store";
import {
  fetchGetUserTournamentStatus,
  fetchChangeUserTournamentStatus,
  selectUserTournamentStatus,
  setUserTournamentStatus,
  TOURNAMENT_USER_TYPES,
} from "@/redux/tournaments";
import { fetchProfileInfo } from "@/redux/profile/settings/getProfileInfo/asyncActions";
import { getProfileInfo } from "@/redux/profile/settings/getProfileInfo/selectors";
import { useCurrentWidth } from "@/hooks";
import { MediaBreakPoints } from "@/resources/constants";
import { getTokenFromStorage } from "@/utils";
import { openAuthModal } from "@/utils/authModal/openAuthModal";
import { ChangeStatusModal } from "@/pages/Tournaments/components/ChangeStatusModal";
import styles from "./GamesList.module.scss";
import padelImage from "@/assets/img/padel-games-list.png";
import arrowIcon from "@/assets/icons/arrowCalendarRightGreen.svg";

export const GamesList: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const windowWidth = useCurrentWidth();
  const breakPoint = MediaBreakPoints.MOBILE_LARGE;

  const { profileInfo } = useAppSelector(getProfileInfo);
  const userTournamentStatus = useAppSelector(selectUserTournamentStatus);

  const [isChangeStatusModalOpen, setIsChangeStatusModalOpen] = useState(false);
  const [isChangingStatus, setIsChangingStatus] = useState(false);
  const [requestStatus, setRequestStatus] = useState<string | null>(null);
  const [modalVisibleContent, setModalVisibleContent] = useState(0);

  const statusFromProfile = profileInfo?.role;
  const finalStatus = userTournamentStatus || statusFromProfile;
  const isParticipant =
    finalStatus === TOURNAMENT_USER_TYPES.PARTICIPANT || finalStatus === "USER";
  const isOrganizer =
    finalStatus === TOURNAMENT_USER_TYPES.ORGANIZER || finalStatus === "ORGANIZATOR";

  useEffect(() => {
    const token = getTokenFromStorage();
    if (!token) return;

    const loadData = async () => {
      try {
        await dispatch(fetchProfileInfo()).unwrap();
        await dispatch(fetchGetUserTournamentStatus()).unwrap();
      } catch (error) {
        console.error("Error loading data:", error);
      }
    };

    loadData();
  }, [dispatch]);

  useEffect(() => {
    if (profileInfo?.role && !userTournamentStatus) {
      dispatch(setUserTournamentStatus(profileInfo.role));
    }
  }, [profileInfo?.role, userTournamentStatus, dispatch]);

  // Если пользователь на главной странице /padel на десктопе, перенаправляем на игры
  useEffect(() => {
    if (location.pathname === "/padel" && windowWidth > breakPoint) {
      navigate("/padel/games", { replace: true });
    }
  }, [location.pathname, navigate, windowWidth, breakPoint]);

  const handleConfirmChangeStatus = async () => {
    setIsChangingStatus(true);
    try {
      const newStatus = await dispatch(
        fetchChangeUserTournamentStatus({ role: "ORGANIZATOR" })
      ).unwrap();

      if (newStatus) {
        setRequestStatus(newStatus);
        setModalVisibleContent(1);
      }
    } catch (error) {
      console.error("Error changing status:", error);
    } finally {
      setIsChangingStatus(false);
    }
  };

  const tournamentStatusContent = useMemo(() => {
    const token = getTokenFromStorage();
    if (!token) return null;

    return (
      <div className={styles.statusContainer}>
        <div className={styles.statusInfoRow}>
          <span className={styles.statusLabel}>Ваш статус:</span>
          {finalStatus ? (
            <span className={styles.statusValue}>
              {isOrganizer ? "Организатор" : "Участник"}
            </span>
          ) : (
            <div className={styles.statusSkeleton} />
          )}
        </div>
        {isParticipant && requestStatus !== "PENDING" && (
          <button
            className={styles.statusButton}
            onClick={() => setIsChangeStatusModalOpen(true)}
          >
            Стать организатором
            <img src={arrowIcon} alt="" />
          </button>
        )}
        {isParticipant && requestStatus === "PENDING" && (
          <span className={styles.statusValue}>Запрос отправлен</span>
        )}
      </div>
    );
  }, [finalStatus, isOrganizer, isParticipant, requestStatus]);

  const topBarItems: TopBarItem[] = useMemo(
    () => [
      {
        id: "games",
        text: "Создатель игры",
        path: "games",
        description: "Найдите компанию и сыграйте в padel вместе!",
      },
      {
        id: "tournaments",
        text: "Турниры Padel",
        path: "tournaments",
        description:
          "Присоединяйтесь к нашим турнирам и станьте частью padel-сообщества!",
        extraContent: tournamentStatusContent,
      },
    ],
    [tournamentStatusContent]
  );

  const getMobileTitle = () => {
    if (location.pathname.startsWith("/padel/games")) return "Создатель игры";
    if (location.pathname.startsWith("/padel/tournaments")) return "Турниры Padel";
    return "Padel";
  };

  const handleItemClick = (item: TopBarItem) => {
    const token = getTokenFromStorage();
    if (!token) {
      openAuthModal(dispatch);
      return;
    }
    navigate(`/padel/${item.path}`);
  };

  return (
    <>
      <Header title={windowWidth <= breakPoint ? getMobileTitle() : "Padel"} />
      <main>
        <section className="container">
          <Title>{windowWidth <= breakPoint ? getMobileTitle() : "Padel"}</Title>
          {windowWidth > breakPoint ? (
            <div className={styles.wrapper}>
              <TopBar
                items={topBarItems}
                basePath="/padel"
                onItemClick={handleItemClick}
                breakPoint={breakPoint}
              />
              <div className={styles.content}>
                <Outlet />
              </div>
            </div>
          ) : (
            <>
              {location.pathname === "/padel" || location.pathname === "/padel/" ? (
                <>
                  <img src={padelImage} alt="Padel" className={styles.padelImage} />
                  <TopBar
                    items={topBarItems}
                    basePath="/padel"
                    onItemClick={handleItemClick}
                    breakPoint={breakPoint}
                  />
                </>
              ) : (
                <div className={styles.wrapper}>
                  <Outlet />
                </div>
              )}
            </>
          )}
        </section>
      </main>
      <ChangeStatusModal
        isOpen={isChangeStatusModalOpen}
        setIsOpen={setIsChangeStatusModalOpen}
        onConfirm={handleConfirmChangeStatus}
        isLoading={isChangingStatus}
        modalVisibleContent={modalVisibleContent}
        setModalVisibleContent={setModalVisibleContent}
      />
    </>
  );
};
